import { Component, OnDestroy, input } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { nodeMetaDataJsonSchema7, SFNodeType } from '@sciflow/schema';
import { selectTemplate } from 'export';
import { DOMSerializer, Node as ProseMirrorNode } from 'prosemirror-model';
import { NodeMetaEditorDialog } from 'projects/ui-components/src';
import { addReferences } from 'projects/reference-management/src/lib/store/references.actions';
import { selectReferenceList } from 'projects/reference-management/src/lib/store/references.reducer';
import { Reference } from 'projects/reference-management/src/lib/reference.model';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { deleteNodeById, updateNodeAttrs } from '../../commands';
import { inferPromotedReferenceFields } from './reference-promotion-parser';
import { EditorView } from 'prosemirror-view';

@Component({
  selector: 'sfo-quick-actions-menu',
  templateUrl: './quick-actions-menu.component.html',
  styleUrls: ['./quick-actions.menu.component.scss'],
  standalone: false,
})
export class QuickActionsMenuComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private activeDialogRef: MatDialogRef<NodeMetaEditorDialog> | null = null;

  readonly editorView = input<EditorView | null>(null);
  readonly node = input<ProseMirrorNode | null>(null);
  readonly SFNodeType = SFNodeType;
  constructor(private dialog: MatDialog, private store: Store) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  deleteNode() {
    const editorView = this.editorView();
    const node = this.node();
    if (!editorView || editorView.isDestroyed) {
      return;
    }
    deleteNodeById(node?.attrs?.id)(editorView.state, editorView.dispatch);
  }

  openEditDialog(renderMode: string) {
    if (this.activeDialogRef) {
      return;
    }

    this.store
      .select(selectTemplate)
      .pipe(take(1))
      .subscribe((template) => {
        const node = this.node();
        const nodeType = node?.type?.name;
        if (!nodeType) {
          return;
        }
        const schema = template?.prosemirrorSchemas?.nodes?.[nodeType] ?? nodeMetaDataJsonSchema7[nodeType];
        this.activeDialogRef = this.dialog.open(NodeMetaEditorDialog, {
          data: {
            node,
            schema,
            renderMode,
          },
        });

        this.activeDialogRef
          .afterClosed()
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.activeDialogRef = null;
            }),
          )
          .subscribe((result) => {
            const editorView = this.editorView();
            const currentNode = this.node();
            if (!editorView || editorView.isDestroyed) {
              return;
            }
            if (result) {
              updateNodeAttrs(currentNode?.attrs?.id, result)(editorView.state, editorView.dispatch);
            }
          });
      });
  }

  /** Promotes a selected bibliography reference node into reducer-backed references. */
  promoteToReference(): void {
    const text = this.serializeSelectedNodeContent();
    const normalizedRawCitation = text?.trim() || '';
    const inferredFields = inferPromotedReferenceFields(normalizedRawCitation);
    const node = this.node();
    const key = node?.attrs?.refId?.trim() || node?.attrs?.id?.trim();
    const nodeId = node?.attrs?.id?.trim();
    if (!key || !nodeId) {
      return;
    }

    this.store
      .select(selectReferenceList)
      .pipe(take(1))
      .subscribe((references) => {
        const alreadyExists = (references || []).some(
          (reference) => reference?.key === key || reference?.csl?.id === key,
        );
        if (alreadyExists) {
          return;
        }

        const newReference: Reference = {
          key,
          csl: {
            id: key,
            ...inferredFields,
            rawCitation: normalizedRawCitation || undefined,
            _meta: {
              source: 'plain-text',
              originalText: normalizedRawCitation,
            },
          },
        };

        this.store.dispatch(addReferences({ references: [newReference] }));

        const editorView = this.editorView();
        if (!editorView || editorView.isDestroyed) {
          return;
        }
        deleteNodeById(nodeId)(editorView.state, editorView.dispatch);
      });
  }

  private serializeSelectedNodeContent(): string {
    const node = this.node();
    const editorView = this.editorView();
    if (!node || !editorView?.state?.schema) {
      return '';
    }
    const serializer = DOMSerializer.fromSchema(editorView.state.schema);
    const wrapper = document.createElement('div');
    wrapper.appendChild(serializer.serializeFragment(node.content));
    return wrapper.innerHTML.trim();
  }
}
